import { UserPermissions, isSuperAdmin } from './permissionEngine';

export const STAFF_ROLES = [
  'administrator',
  'shop_manager',
  'editor',
  'super_admin_business',
  'admin_business',
  'reservation_business',
  'account_business',
  'operations_business',
  'agent_business',
] as const;

export const CUSTOMER_PORTAL_ROLES = ['customer', 'subscriber'] as const;

const STAFF_CAPABILITIES = [
  'manage_options',
  'manage_woocommerce',
  'create_users',
  'edit_others_shop_orders',
  'list_users',
];

export interface PortalUserIdentity {
  id?: string;
  databaseId?: number;
  email?: string | null;
}

export interface OwnableOrder {
  customer?: {
    databaseId?: number;
    email?: string | null;
  } | null;
  billing?: {
    email?: string | null;
  } | null;
}

export function parseDatabaseId(
  globalId?: string | null,
  databaseId?: number | null
): number | undefined {
  if (typeof databaseId === 'number' && Number.isFinite(databaseId) && databaseId > 0) {
    return databaseId;
  }
  if (!globalId) return undefined;
  if (/^\d+$/.test(globalId)) return Number(globalId);
  try {
    const decoded = atob(globalId);
    const match = decoded.match(/:(\d+)\s*$/) || decoded.match(/(\d+)/);
    if (match) {
      const parsed = Number(match[1]);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  } catch {
    // Ignore invalid global ids.
  }
  return undefined;
}

export function isStaffUser(userPermissions: UserPermissions): boolean {
  if (isSuperAdmin(userPermissions)) return true;
  if (STAFF_ROLES.some((role) => userPermissions.roles.has(role))) return true;
  return STAFF_CAPABILITIES.some((cap) => userPermissions.capabilities.has(cap));
}

export function isCustomerPortalUser(userPermissions: UserPermissions): boolean {
  if (isStaffUser(userPermissions)) return false;
  return CUSTOMER_PORTAL_ROLES.some((role) => userPermissions.roles.has(role));
}

export function getCustomerOrdersWhere(user: PortalUserIdentity | null): { customerId: number } | undefined {
  const customerId = user?.databaseId;
  if (!customerId) return undefined;
  return { customerId };
}

function normalizeEmail(email?: string | null): string | null {
  const value = (email || '').trim().toLowerCase();
  return value || null;
}

export function isOwnOrder(
  order: OwnableOrder | null | undefined,
  user: PortalUserIdentity | null | undefined
): boolean {
  if (!order || !user) return false;

  const userId = user.databaseId;
  const customerId = order.customer?.databaseId;
  if (userId && customerId && userId === customerId) return true;

  const userEmail = normalizeEmail(user.email);
  if (!userEmail) return false;

  return (
    normalizeEmail(order.customer?.email) === userEmail ||
    normalizeEmail(order.billing?.email) === userEmail
  );
}

export function filterOwnOrders<T extends OwnableOrder>(
  orders: T[],
  user: PortalUserIdentity | null | undefined
): T[] {
  if (!user) return [];
  return orders.filter((order) => isOwnOrder(order, user));
}

const CUSTOMER_CANCEL_REQUEST_STATUSES = new Set(['PENDING', 'ON_HOLD', 'PROCESSING']);

export function canCustomerRequestCancel(status?: string | null): boolean {
  const current = (status || '').toUpperCase().replace(/\s+/g, '_').replace(/-/g, '_');
  return CUSTOMER_CANCEL_REQUEST_STATUSES.has(current);
}

export function isCustomerAllowedPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  const allowed = new Set([
    '/dashboard',
    '/profile',
    '/orders-list',
    '/bookings-list',
    '/tours-list',
    '/hotels-list',
    '/transfers-list',
    '/invoices-list',
    '/invoices-detail',
    '/apps-booking-calendar',
    '/security-settings',
    '/access-denied',
  ]);
  if (allowed.has(path)) return true;
  if (path.startsWith('/invoices-detail/')) return true;
  if (/^\/orders\/[^/]+$/.test(path)) return true;
  if (/^\/bookings\/[^/]+$/.test(path)) return true;
  return false;
}
